import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CrmPage from "./CrmPage";
import { crmFetch, stageInfo, formatMoney, calendarDate } from "./crmApi";
import "./Crm.css";

const MONTH_LABEL = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

/**
 * The shoot calendar, from the sales side: every lead that has an event date
 * and hasn't been lost, grouped by month. Answers "am I free that weekend?"
 * and "what's coming up?" without leaving the CRM.
 */
function CrmCalendar() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await crmFetch("/api/crm/calendar");
      setBookings(d.bookings || []);
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const { upcoming, past } = useMemo(() => {
    const up = [];
    const pa = [];
    for (const b of bookings) {
      const d = calendarDate(b.event_date);
      if (!d) continue;
      d.setHours(0, 0, 0, 0);
      (d >= today ? up : pa).push(b);
    }
    pa.reverse(); // most recent first when looking backwards
    return { upcoming: up, past: pa };
  }, [bookings, today]);

  const groupByMonth = (list) => {
    const groups = new Map();
    for (const b of list) {
      const d = calendarDate(b.event_date);
      if (!d) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(b);
    }
    return [...groups.entries()];
  };

  // Two shoots on the same date is worth surfacing loudly — it's the mistake
  // this page exists to prevent.
  const clashDates = useMemo(() => {
    const seen = new Map();
    for (const b of upcoming) {
      const key = String(b.event_date).slice(0, 10);
      seen.set(key, (seen.get(key) || 0) + 1);
    }
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  }, [upcoming]);

  const renderRow = (b) => {
    const st = stageInfo(b.stage);
    const dateKey = String(b.event_date).slice(0, 10);
    const d = calendarDate(b.event_date) || new Date();
    return (
      <div
        key={b.id}
        className="crm-cal-row"
        onClick={() => navigate(`/crm/leads?lead=${b.id}`)}
      >
        <div className="crm-cal-date" style={{ borderColor: st.color }}>
          <span className="crm-cal-day">{d.getDate()}</span>
          <span className="crm-cal-dow">
            {d.toLocaleDateString("en-US", { weekday: "short" })}
          </span>
        </div>
        <div className="crm-cal-body">
          <div className="crm-row-title">
            {b.client_name}
            {clashDates.has(dateKey) && (
              <span className="crm-clash">⚠️ 2+ shoots this day</span>
            )}
          </div>
          <div className="crm-row-sub">
            {[b.event_type, b.venue, b.client_phone].filter(Boolean).join(" · ") || "No details yet"}
          </div>
          {b.linked_event_name && (
            <div className="crm-row-sub">🎉 Linked to event: {b.linked_event_name}</div>
          )}
        </div>
        <div className="crm-cal-right">
          <span className="crm-badge" style={{ background: `${st.color}22`, color: st.color }}>
            {st.icon} {st.label}
          </span>
          {formatMoney(b.quoted_amount) && (
            <span className="crm-lead-amount">{formatMoney(b.quoted_amount)}</span>
          )}
        </div>
      </div>
    );
  };

  const bookedCount = upcoming.filter((b) => b.stage === "booked").length;

  return (
    <CrmPage
      title="📅 Calendar"
      subtitle="Confirmed shoots and pencilled-in dates, by month"
      loading={loading}
      planBlocked={planBlocked}
      actions={
        <button className="crm-primary-btn" onClick={() => navigate("/crm/leads?new=1")}>
          ＋ New Lead
        </button>
      }
    >
      {bookings.length === 0 ? (
        <div className="crm-empty">
          <span className="crm-empty-icon">📅</span>
          Nothing scheduled yet.
          <div className="crm-empty-hint">
            Give a lead an event date and it appears here — booked or not — so you can spot
            clashes before you promise a date.
          </div>
        </div>
      ) : (
        <>
          <div className="crm-stats-row">
            <div className="crm-stat-card" style={{ borderTopColor: "#10b981" }}>
              <div className="crm-stat-value">{bookedCount}</div>
              <div className="crm-stat-label">✅ Confirmed shoots ahead</div>
            </div>
            <div className="crm-stat-card" style={{ borderTopColor: "#f59e0b" }}>
              <div className="crm-stat-value">{upcoming.length - bookedCount}</div>
              <div className="crm-stat-label">🕐 Dates still in play</div>
            </div>
            {clashDates.size > 0 && (
              <div className="crm-stat-card" style={{ borderTopColor: "#dc2626" }}>
                <div className="crm-stat-value">{clashDates.size}</div>
                <div className="crm-stat-label">⚠️ Days with a clash</div>
              </div>
            )}
          </div>

          {upcoming.length === 0 ? (
            <div className="crm-empty">No upcoming dates — everything on file is in the past.</div>
          ) : (
            groupByMonth(upcoming).map(([month, list]) => (
              <div className="crm-task-group" key={month}>
                <h3 className="crm-task-group-title upcoming">{MONTH_LABEL(month)} ({list.length})</h3>
                {list.map(renderRow)}
              </div>
            ))
          )}

          {past.length > 0 && (
            <>
              <button className="crm-link-btn" onClick={() => setShowPast(!showPast)}>
                {showPast ? "Hide" : "Show"} past dates ({past.length})
              </button>
              {showPast && groupByMonth(past).map(([month, list]) => (
                <div className="crm-task-group" key={month} style={{ opacity: 0.7 }}>
                  <h3 className="crm-task-group-title someday">{MONTH_LABEL(month)} ({list.length})</h3>
                  {list.map(renderRow)}
                </div>
              ))}
            </>
          )}
        </>
      )}
    </CrmPage>
  );
}

export default CrmCalendar;
